import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Cable,
  Server,
  Cpu,
  Camera,
  Wrench,
  Radio,
  Activity,
  Wifi,
  Tag,
  Network,
  Zap,
  ScanLine,
  Fingerprint,
  Siren,
  Car,
  MonitorSmartphone,
  ArrowUpRight,
} from "lucide-react";

const CATEGORIES = [
  {
    id: "infra",
    title: "Network Infrastructure",
    blurb: "Structured cabling, racks, splicing & certified testing.",
    Icon: Server,
    image:
      "https://images.unsplash.com/photo-1680691257251-5fead813b73e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    services: [
      { Icon: Tag, name: "Cable Identification & Labeling", desc: "Organised, clearly labeled cabling for easy maintenance and troubleshooting." },
      { Icon: Server, name: "Network Rack Dressing", desc: "Neat, professional rack arrangement for optimal airflow and performance." },
      { Icon: Cable, name: "OFC Splicing", desc: "High-precision fiber optic splicing for reliable, low-loss connectivity." },
      { Icon: ScanLine, name: "OTDR Testing", desc: "Fiber link testing using OTDR to ensure quality and performance." },
      { Icon: Activity, name: "Fluke Testing & Certification", desc: "Certified testing and validation of network cabling systems." },
    ],
  },
  {
    id: "engineering",
    title: "Specialized Engineering",
    badge: "Premium",
    blurb: "Health, performance & coverage — measured and optimised.",
    Icon: Activity,
    image:
      "https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    services: [
      { Icon: Activity, name: "Network Health Checkup", desc: "Comprehensive network assessment to identify issues and ensure optimal performance." },
      { Icon: Radio, name: "Performance Monitoring", desc: "Real-time monitoring and analytics to keep your network running at peak efficiency." },
      { Icon: Wifi, name: "Wireless Heatmap Creation", desc: "Professional heatmap analysis to optimise Wi-Fi coverage and eliminate dead zones." },
    ],
  },
  {
    id: "security",
    title: "Security & Access",
    blurb: "CCTV, biometrics, alarms and vehicle tracking — unified.",
    Icon: Camera,
    image:
      "https://images.unsplash.com/photo-1589935447067-5531094415d1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    services: [
      { Icon: Camera, name: "CCTV & Solar CCTV Cameras", desc: "HD surveillance with solar-powered options for off-grid sites." },
      { Icon: Fingerprint, name: "Biometric Attendance & Door Phones", desc: "Touchless attendance and secure access control." },
      { Icon: Siren, name: "Security Alarms", desc: "Intrusion detection and emergency alarm systems." },
      { Icon: Car, name: "Vehicle Tracking", desc: "GPS tracking and fleet visibility solutions." },
    ],
  },
  {
    id: "fiber",
    title: "Networking & Fiber",
    blurb: "Design, deploy and configure end-to-end networks.",
    Icon: Network,
    image:
      "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    services: [
      { Icon: Network, name: "Network Infra Design & Wi-Fi Setup", desc: "Architect Wi-Fi, LAN and WAN that scales with your business." },
      { Icon: Cable, name: "Fiber Splicing, Termination & FTTH", desc: "Single & multi-mode fiber, last-mile FTTH deployment." },
      { Icon: Cpu, name: "Layer 2/3 Config & Performance Testing", desc: "VLAN, routing, QoS and certified throughput testing." },
    ],
  },
  {
    id: "hardware",
    title: "Hardware & Electrical",
    blurb: "AV, electrical panels and on-site troubleshooting.",
    Icon: Wrench,
    image:
      "https://images.unsplash.com/photo-1633265486064-086b219458ec?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    services: [
      { Icon: MonitorSmartphone, name: "TV, Computer & Projector Setup/Repair", desc: "AV install, calibration and repair for offices and venues." },
      { Icon: Zap, name: "Electrical Panel Installation", desc: "Safe and code-compliant electrical panels and connections." },
      { Icon: Wrench, name: "Comprehensive Troubleshooting", desc: "Diagnose and resolve hardware, wiring and connectivity issues." },
    ],
  },
];

const Services = () => {
  const [active, setActive] = useState(CATEGORIES[0].id);
  const current = CATEGORIES.find((c) => c.id === active);

  return (
    <section id="services" data-testid="services-section" className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-grid-faint" />
      <div className="orb bg-[#FF6B00]/15 w-[500px] h-[500px] -top-40 left-1/4" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF6B00] mb-4">
              ◆ Services
            </div>
            <h2 data-testid="services-title" className="font-display text-4xl sm:text-5xl font-bold leading-[1.05] tracking-tighter">
              Built for uptime. <br />
              <span className="text-[#A0B2C6]">Engineered for scale.</span>
            </h2>
          </div>
          <p className="md:max-w-sm text-[#A0B2C6] leading-relaxed">
            A complete portfolio across infrastructure, security, fiber, hardware and engineering — delivered as one cohesive service.
          </p>
        </div>

        {/* Tabs */}
        <div data-testid="services-tabs" className="flex flex-wrap gap-2 mb-10">
          {CATEGORIES.map((c) => {
            const isActive = c.id === active;
            return (
              <button
                key={c.id}
                data-testid={`service-tab-${c.id}`}
                onClick={() => setActive(c.id)}
                className={`group inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium tracking-wide border transition-all ${
                  isActive
                    ? "bg-[#FF6B00] border-[#FF6B00] text-white shadow-[0_0_20px_rgba(255,107,0,0.35)]"
                    : "border-white/10 bg-[#0B1320]/60 text-[#A0B2C6] hover:text-white hover:border-white/25"
                }`}
              >
                <c.Icon size={15} strokeWidth={1.8} />
                {c.title}
                {c.badge && (
                  <span className={`font-mono text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 ${isActive ? "bg-white/20" : "bg-[#FF6B00]/20 text-[#FF6B00]"}`}>
                    {c.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <motion.div
          key={current.id}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="grid lg:grid-cols-12 gap-6"
          data-testid="services-grid"
        >
          {/* Featured image card */}
          <div className="lg:col-span-5 relative beam-border rounded-2xl overflow-hidden border border-white/10 bg-[#0B1320] min-h-[420px]">
            <img src={current.image} alt={current.title} className="absolute inset-0 w-full h-full object-cover opacity-70" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#060B14] via-[#060B14]/50 to-transparent" />
            <div className="relative h-full flex flex-col justify-between p-7">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A0B2C6]">
                  Category · 0{CATEGORIES.findIndex((x) => x.id === active) + 1} / {String(CATEGORIES.length).padStart(2, "0")}
                </span>
                {current.badge && (
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF6B00] border border-[#FF6B00]/40 px-2 py-1">
                    {current.badge} Service
                  </span>
                )}
              </div>
              <div>
                <h3 className="font-display text-3xl sm:text-4xl font-bold leading-tight tracking-tight">
                  {current.title}
                </h3>
                <p className="mt-3 text-[#A0B2C6] max-w-md">{current.blurb}</p>
                <a
                  href="#contact"
                  data-testid={`service-cta-${current.id}`}
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-white border-b border-[#FF6B00] pb-1 hover:text-[#FF6B00] transition"
                >
                  Request a consultation <ArrowUpRight size={16} />
                </a>
              </div>
            </div>
          </div>

          {/* Service items */}
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {current.services.map((s) => (
              <div
                key={s.name}
                data-testid={`service-item-${s.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                className="beam-border rounded-2xl bg-[#0B1320] border border-white/8 p-6 hover:bg-[#111C2E] transition-all group"
              >
                <div className="w-11 h-11 rounded-xl bg-[#FF6B00]/12 border border-[#FF6B00]/25 flex items-center justify-center text-[#FF6B00] mb-4 group-hover:scale-110 transition-transform">
                  <s.Icon size={20} strokeWidth={1.6} />
                </div>
                <h4 className="font-display text-lg font-semibold tracking-tight">{s.name}</h4>
                <p className="mt-2 text-sm text-[#A0B2C6] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
